/**
 * C-Market Intel — one consumer, one page.
 *
 * WHY THIS EXISTS. Market Intel is a SINGLE consumer, and it had five sidebar
 * entries: health, lane prices, heat map, lane explorer, SQL plans. That made
 * the nav a list of views rather than a list of things, and it buried the one
 * fact a reader needs first — which side of the topic they are looking at.
 * Five views of one consumer belong behind one entry.
 *
 * Tabs are ROUTED, not local state (`/load-pipeline/market-intel/:tab`), so a
 * tab can be linked to and the back button does what it looks like it does. A
 * view someone can only reach by clicking is a view nobody can point a
 * colleague at.
 *
 * TWO KINDS OF TAB. Health and Lane Explorer are the operator AUDIT — what the
 * consumer wrote, read through main-service. Serving Performance times the
 * public journeys through the same path. Lane Prices and Heat Map are MARKET DATA — the public Market Intel API, the same answers
 * every other client gets, over the snapshot's fixed window. So each tab owns
 * its own controls: the audit tabs pick an hours window, the market tabs show
 * the snapshot's window and age instead of pretending to pick one.
 */

import Box from '@mui/joy/Box'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import ConsumerPage from './ConsumerPage'
import HeatmapPage from './HeatmapPage'
import LanePricesPage from './LanePricesPage'
import LanesPage from './LanesPage'
import ServingPerfPage from './ServingPerfPage'

const TABS = [
    { key: 'health', label: 'Health', render: () => <ConsumerPage /> },
    { key: 'lane-prices', label: 'Lane Prices', render: () => <LanePricesPage /> },
    { key: 'heatmap', label: 'Heat Map', render: () => <HeatmapPage /> },
    { key: 'lanes', label: 'Lane Explorer', render: () => <LanesPage /> },
    { key: 'serving', label: 'Serving Performance', render: () => <ServingPerfPage /> },
] as const

const DEFAULT_TAB = TABS[0].key

export default function LoadPipelineMarketIntelPage() {
    const { tab } = useParams<{ tab?: string }>()
    const navigate = useNavigate()
    const active = TABS.some((t) => t.key === tab) ? (tab as string) : DEFAULT_TAB

    // The tab used to be SQL Performance at `…/sql`; a bookmark to it should land on what replaced it.
    if (tab === 'sql') return <Navigate to="/load-pipeline/market-intel/serving" replace />

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 0.5 }}>
                C-Market Intel
            </Typography>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                Reads <code>loadpipe-observations-intel-sub</code>, folds rates into hourly histograms, flushes once a minute.
            </Typography>

            <Tabs value={active} onChange={(_, value) => navigate(`/load-pipeline/market-intel/${value}`)} sx={{ bgcolor: 'transparent' }}>
                <TabList tabFlex="none" sx={{ mb: 1 }}>
                    {TABS.map((t) => (
                        <Tab key={t.key} value={t.key} variant="plain" color="neutral">
                            {t.label}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {/* Only the active tab is mounted. Each one issues its own queries
                against a small instance the consumer is writing to, so rendering
                five at once would multiply an audit page into a load source. */}
            {TABS.find((t) => t.key === active)?.render()}
        </Box>
    )
}
